import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Container,
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';

const Auth0Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();

  // Invite/reset landing: Auth0's "Password Changed!" page sends users here —
  // via the app's Application Login URI (Auth0 appends ?iss=, the OIDC
  // third-party-initiated-login marker) or, on Classic tenants, via the
  // ticket's result_url (?welcome=1&email=). Either way, bounce straight into
  // the hosted sign-in so the user just types their new password once and
  // lands on the dashboard.
  const isWelcome = searchParams.get('welcome') === '1' || searchParams.has('iss');
  const welcomeEmail = searchParams.get('email') || undefined;

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    if (isWelcome && !isAuthenticated && !isLoading) {
      loginWithRedirect({
        appState: { returnTo: '/dashboard' },
        authorizationParams: welcomeEmail ? { login_hint: welcomeEmail } : undefined,
      });
    }
  }, [isWelcome, welcomeEmail, isAuthenticated, isLoading, loginWithRedirect]);

  const handleLogin = () => {
    loginWithRedirect({
      appState: {
        returnTo: '/dashboard'
      }
    });
  };

  if (isWelcome && !isAuthenticated) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Taking you to sign in…
          </Typography>
        </Box>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Paper sx={{ p: 4, width: '100%' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <LoginIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" component="h1">
              PlanScout Advisor Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Sign in to access your dashboard
            </Typography>
          </Box>

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleLogin}
            disabled={isLoading}
            startIcon={<LoginIcon />}
            sx={{ mt: 3 }}
          >
            Sign In
          </Button>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 2, textAlign: 'center' }}
          >
            Secure login powered by Auth0
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default Auth0Login;
